import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Box,
  Typography,
  Button,
} from '@mui/material';
import styles from './GenericDialog.styles';

function a11yProps(index) {
  return {
    id: `generic-tab-${index}`,
    'aria-controls': `generic-tabpanel-${index}`,
  };
}

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`generic-tabpanel-${index}`}
      aria-labelledby={`generic-tab-${index}`}
      style={styles.tabPanel}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function GenericDialog({
  open,
  onClose,
  title,
  subtitle,
  tabs,
  defaultTabKey,
  maxWidth = 'sm',
  fullWidth = true,
  onTabChange,
  renderGlobalActions,
  renderHeaderExtras,
}) {
  const keys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const initialIndex = Math.max(0, keys.indexOf(defaultTabKey ?? keys[0]));
  const [tabIndex, setTabIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(false);

  const keysString = keys.join('|');
  useEffect(() => {
    const idx = Math.max(0, keys.indexOf(defaultTabKey ?? keys[0]));
    setTabIndex(idx);
  }, [defaultTabKey, keys, keysString]);

  const activeKey = keys[tabIndex];
  const setActiveKey = (key) => {
    const i = Math.max(0, keys.indexOf(key));
    setTabIndex(i);
    onTabChange && onTabChange(key);
  };

  const current = tabs[tabIndex];
  const helpers = {
    close: onClose,
    activeKey,
    setActiveKey,
    loading,
    setLoading,
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth={fullWidth} maxWidth={maxWidth}>
      <Box sx={{ pt: 2 }}>
        <DialogTitle sx={styles.title}>{title}</DialogTitle>
        {subtitle ? (
          <Typography variant="body2" sx={styles.subtitle}>
            {subtitle}
          </Typography>
        ) : null}

        <Box sx={styles.cardTabsHeader}>
          <Tabs
            value={tabIndex}
            onChange={(_e, idx) => setActiveKey(keys[idx])}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={styles.cardTabsBar}
          >
            {tabs.map((t, i) => (
              <Tab key={t.key} label={t.label} {...a11yProps(i)} />
            ))}
          </Tabs>

          {renderHeaderExtras ? (
            <Box sx={styles.headerControls}>
              {renderHeaderExtras(activeKey, helpers)}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Divider />

      <DialogContent sx={styles.content}>
        {tabs.map((t, i) => (
          <TabPanel key={t.key} value={tabIndex} index={i}>
            {t.render ? t.render(helpers) : null}
          </TabPanel>
        ))}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        {current && current.actions
          ? current.actions(helpers)
          : renderGlobalActions
          ? renderGlobalActions(helpers)
          : (
            <Button
              onClick={onClose}
              variant="contained"
              sx={styles.btnSecondary}
              disabled={loading}
            >
              Close
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
}



